// packages/common/src/sse/SSEManager.ts
import { auth } from '../auth/firebaseClient';
import { runtimeConfig } from '../config/runtimeConfig';

export type Handler = (payload: Record<string, unknown>) => void;

function getApiBase(): string {
  return (runtimeConfig('API_URL') ?? 'http://localhost:8080').replace(/\/$/, '');
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Full-jitter backoff (AWS "Exponential Backoff And Jitter"): a random delay in
 * [FLOOR_MS, ms]. Spreading reconnect times prevents a thundering herd when many
 * clients reconnect at once (e.g. after a deploy) and breaks hot-loops when the
 * server displaces excess connections. Floored so a fast retry never busy-spins.
 */
const RECONNECT_FLOOR_MS = 250;
function jitter(ms: number): number {
  return Math.max(RECONNECT_FLOOR_MS, Math.random() * ms);
}

class SSEManager {
  private readonly url: string;
  private controller: AbortController | null = null;
  private subscribers = new Map<string, Set<Handler>>();
  /** Set of event IDs seen since last connect — prevents duplicate dispatch on reconnect */
  private seenIds = new Set<string>();
  /** Tracks the most recent SSE `id:` field — sent as `Last-Event-ID` on reconnect */
  private lastEventId: string | null = null;
  private retryMs = 1_000;
  private cancelled = false;
  private isConnecting = false;
  /**
   * Called after every unintentional stream close + reconnect (not on deliberate
   * disconnect). Allows callers to invalidate queries that may have missed events
   * during the gap. The BE replays via Last-Event-ID but the replay window is
   * bounded — this is the safety net for gaps longer than the ring buffer.
   *
   * A Set, not a single nullable callback: this is one shared connection
   * serving multiple independent query-invalidation bridges (roster,
   * outreach, ...), each mounted/unmounted on its own page's lifecycle. A
   * single slot let the second bridge to register clobber the first's
   * handler instead of running both.
   */
  private reconnectHandlers = new Set<() => void>();

  constructor(url: string) {
    this.url = url;
  }

  /**
   * Register a callback that fires after every unintentional reconnect.
   * Returns an unsubscribe function — call it in useEffect cleanup. Intended
   * for query invalidation catch-up.
   */
  addReconnectHandler(fn: () => void): () => void {
    this.reconnectHandlers.add(fn);
    return () => {
      this.reconnectHandlers.delete(fn);
    };
  }

  /**
   * Subscribe to a specific SSE event type.
   * Opens the connection when the first subscriber registers.
   * Returns an unsubscribe function — call it in useEffect cleanup.
   */
  subscribe(eventType: string, handler: Handler): () => void {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set());
    }
    this.subscribers.get(eventType)!.add(handler);

    if (this.controller === null && !this.isConnecting) {
      this.cancelled = false;
      this.isConnecting = true;
      void this.connect();
    }

    return () => {
      const set = this.subscribers.get(eventType);
      set?.delete(handler);
      if (set?.size === 0) this.subscribers.delete(eventType);
      const total = [...this.subscribers.values()].reduce((n, s) => n + s.size, 0);
      if (total === 0) this.disconnect();
    };
  }

  private async connect(): Promise<void> {
    while (!this.cancelled) {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          await wait(2_000);
          continue;
        }

        this.controller = new AbortController();

        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        };
        if (this.lastEventId !== null) {
          headers['Last-Event-ID'] = this.lastEventId;
        }

        const response = await fetch(this.url, {
          headers,
          signal: this.controller.signal,
          cache: 'no-store',
        });

        if (!response.ok || !response.body) {
          if (response.status === 429) {
            if (this.retryMs < 30_000) {
              console.warn('[SSEManager] 429 — SSE connection cap; backing off to 30s.');
            }
            this.retryMs = 30_000;
          } else if (response.status === 401) {
            // Force token refresh so next attempt gets a fresh token
            try { await auth.currentUser?.getIdToken(true); } catch { /* ignore */ }
          }
          throw new Error(`sse: HTTP ${response.status}`);
        }

        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('text/event-stream')) {
          throw new Error(`SSE: unexpected content-type: ${contentType}`);
        }

        this.retryMs = 1_000; // reset on successful connect
        await this.readStream(response.body);

        // Stream ended unintentionally (server close/deploy, displaced by a newer
        // connection from the same user, or a network drop). Fire the catch-up
        // handler so callers can invalidate queries that may have missed events,
        // then ALWAYS wait a jittered interval before reconnecting. Without this,
        // a clean stream-end reconnects instantly — which stampedes the backend
        // on every deploy and hot-loops if the server is displacing connections.
        if (this.cancelled) break;
        this.reconnectHandlers.forEach((fn) => fn());
        await wait(jitter(this.retryMs));

      } catch {
        if (this.cancelled) {
          this.isConnecting = false;
          return;
        }
        await wait(jitter(this.retryMs));
        this.retryMs = Math.min(this.retryMs * 2, 30_000);
      }
    }
    this.isConnecting = false;
  }

  private async readStream(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (!this.cancelled) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx = buffer.indexOf('\n\n');
        while (idx !== -1) {
          this.dispatchFrame(buffer.slice(0, idx));
          buffer = buffer.slice(idx + 2);
          idx = buffer.indexOf('\n\n');
        }
      }
    } finally {
      reader.cancel().catch(() => {/* ignore cancel errors */});
    }
  }

  private dispatchFrame(frame: string): void {
    // Parse all SSE fields per spec (event:, data:, id:, retry:, :comment)
    let eventType = 'message';
    const dataLines: string[] = [];
    let id: string | null = null;

    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        const data = line.slice(5).trim();
        if (data) dataLines.push(data); // empty data = heartbeat — skip
      } else if (line.startsWith('id:')) {
        id = line.slice(3).trim();
      } else if (line.startsWith('retry:')) {
        const ms = parseInt(line.slice(6).trim(), 10);
        // Clamp to [1000, 60000] — prevents server from forcing a tight reconnect loop (SEC-003)
        if (!Number.isNaN(ms)) this.retryMs = Math.max(1_000, Math.min(ms, 60_000));
      }
      // Lines starting with ':' are SSE comments — intentionally ignored
    }

    // Update last-event-id for reconnect header
    if (id !== null) this.lastEventId = id;

    // Heartbeat / comment-only frame — nothing to dispatch
    if (dataLines.length === 0) return;

    // Deduplicate across reconnect boundary
    if (id !== null) {
      if (this.seenIds.has(id)) return;
      this.seenIds.add(id);
      // Bound seenIds memory: evict oldest 50 when it exceeds 200
      if (this.seenIds.size > 200) {
        const it = this.seenIds.values();
        for (let i = 0; i < 50; i++) {
          const { value, done } = it.next();
          if (done) break;
          this.seenIds.delete(value);
        }
      }
    }

    const jsonStr = dataLines.join('\n');
    try {
      // Support both JSON-envelope format {event_type, payload} and native SSE event: field
      const raw = JSON.parse(jsonStr) as Record<string, unknown>;
      // If the BE still sends event_type inside the JSON body, honour it
      const resolvedType = (raw.event_type as string | undefined) ?? eventType;
      const payload = (raw.payload as Record<string, unknown> | undefined) ?? raw;

      this.subscribers.get(resolvedType)?.forEach(h => h(payload));
    } catch {
      if (import.meta.env.DEV) {
        console.warn('[SSEManager] malformed SSE data frame (skipped):', jsonStr.slice(0, 200));
      }
    }
  }

  /**
   * Disconnect and reset all reconnect state so a subsequent `subscribe()`
   * call can start a fresh connection. Call this on user sign-out (SEC-004/006).
   */
  disconnect(): void {
    this.cancelled = true;
    this.isConnecting = false; // prevent subscribe() guard from blocking restart (SEC-004)
    this.lastEventId = null;   // fresh replay window on next login
    this.seenIds.clear();      // discard prior session's dedup set (SEC-006)
    this.controller?.abort();
    this.controller = null;
  }
}

/**
 * Singleton SSEManager — one connection covers all topics.
 * Topics param is merged so a single stream serves all subscribers.
 */
export const sseManager = new SSEManager(
  `${getApiBase()}/api/v1/sse/events?topics=ai,roster,notifications,dashboard,import,export,outreach`,
);
